/**
 * ScrollableOverflow — horizontal scroll container with fade edges and arrow buttons.
 * Used for diagrams and other content that may exceed container width.
 * Shows gradient fades and chevron buttons only when content overflows.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ScrollableOverflowProps {
  children: ReactNode;
  className?: string;
}

const SCROLL_AMOUNT = 200;

export function ScrollableOverflow({ children, className }: ScrollableOverflowProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    el.addEventListener('scroll', updateScrollState, { passive: true });

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);

    // Also observe children size changes (e.g. SVG rendered async)
    const mutationObserver = new MutationObserver(updateScrollState);
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [updateScrollState]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  }, []);

  const hasOverflow = canScrollLeft || canScrollRight;

  return (
    <div className={className} style={{ position: 'relative' }}>
      {/* Scrollable content */}
      <div
        ref={scrollRef}
        style={{
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {children}
      </div>

      {/* Hide webkit scrollbar */}
      <style>{`
        .f-scrollable-hide::-webkit-scrollbar { display: none; }
      `}</style>

      {hasOverflow && (
        <>
          {/* Left fade + button */}
          {canScrollLeft && (
            <>
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: 48,
                  background: 'linear-gradient(to right, var(--f-bg), transparent)',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
              <button
                type="button"
                onClick={() => scroll('left')}
                aria-label="Scroll left"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: 4,
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: '1px solid var(--f-border)',
                  background: 'var(--f-bg-raised)',
                  color: 'var(--f-fg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: 'var(--f-shadow-sm)',
                }}
              >
                <ChevronLeft size={16} />
              </button>
            </>
          )}

          {/* Right fade + button */}
          {canScrollRight && (
            <>
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: 48,
                  background: 'linear-gradient(to left, var(--f-bg), transparent)',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
              <button
                type="button"
                onClick={() => scroll('right')}
                aria-label="Scroll right"
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: 4,
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: '1px solid var(--f-border)',
                  background: 'var(--f-bg-raised)',
                  color: 'var(--f-fg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: 'var(--f-shadow-sm)',
                }}
              >
                <ChevronRight size={16} />
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
